/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import initModalMainUrl from "url:../assets/images/init_modal_main.png";
import initModalUploaderUrl from "url:../assets/images/init_modal_uploader.png";
import initModalUploadedUrl from "url:../assets/images/init_modal_uploaded.png";
import initModalConfirmUrl from "url:../assets/images/init_modal_confirm.png";
import initModalResultsUrl from "url:../assets/images/init_modal_results.png";

export const initializationModalContent = `
## Summary

The Initialization Modal is your starting point for creating transformation pipelines in Dexter. It automatically appears when you first navigate to an empty Transform Engine, providing a guided workflow to quickly set up your mapping.

The modal offers two options to begin working:

<img src="${initModalMainUrl}" alt="Initialization Modal Main" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

1. **Upload Files and Recommend Transform** - Upload data files and let Dexter analyze them to suggest appropriate transformations after their type is detected
2. **Select a Previous Transform Session** - Reload a previously saved mapping to continue where you left off
3. **Cancel** - Cancellation cleans up any processes that have happened so far.

You can reopen this modal at any time by clicking the initialization button in the toolbar, even if your canvas already has nodes.

---

## When It Appears

The Initialization Modal automatically opens when:
- You first navigate to the Transform Engine with an empty canvas
- It only appears once per browser session automatically
- You can manually reopen it anytime using the initialization button in the toolbar

---

## Upload Files and Recommend Transform

This workflow helps you quickly upload source files and create input nodes on your canvas.

The upload workflow consists of three stages:
1. **Upload** - Select and upload your data files
2. **Verification** - Confirm file types detected by Dexter
3. **Confirmation** - Review successful uploads before adding to canvas

### Step-by-Step Process

#### Step 1: Select Files to Upload

When you select "Upload Files and Recommend Transform", you'll see the file upload interface:

<img src="${initModalUploaderUrl}" alt="Initialization Modal Uploader" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**Supported File Types:**
- Java (.java)
- C (.c)
- C++ (.cpp)
- C++ Header Files (.h)
- Interface Definition Language (.idl)
- Python (.py)
- Protobuf (.proto, .pb2)
- XML (.xml)
- JavaScript (.js)
- PDF (.pdf) - Automatically parsed to extract structured data type (Protobuf) from above

**Upload Limits:**
- Maximum file size: 10MB per file
- No limit on number of files
- Files can be uploaded individually or in batches

**How to Upload:**
1. Click "Browse files" or drag and drop files into the upload area
2. Multiple files can be selected at once
3. Files begin uploading immediately
4. Upload progress is shown for each file

If a file fails to upload, the reason is displayed below the file.

#### Step 2: File Type Detection

<img src="${initModalUploadedUrl}" alt="Initialization Modal Uploaded" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

After files are uploaded, appearing in the area at **1**, Dexter automatically analyzes each file to detect its type, regardless of the file extension the file has.

**Detection Process:**
- Dexter's AI classifier examines the file content
- File type is predicted based on structure and patterns, and its status is shown at **3**
- Completes within seconds for most files, providing the predicted file type at **2**

Files can be removed at any time using the trashcan icon at **4**.

**File Type Column Information:**
- Shows detected file type in a dropdown
- You can override Dexter's prediction if the machine learning algorithm's prediction is incorrect
- For PDFs, displays ".pdf" until parsing completes and a viable data format is extracted from it

**Why Verify?**
- Dexter's classifier is accurate but not perfect
- Some files may have ambiguous structures
- Correct file types ensure proper parsing

In the future, Dexter will use the discrepancy between the predicted file type and the user-selected file type to retrain its model.

#### Special Case: PDF Parsing

PDF files receive special handling:

**Automatic Parsing:**
- PDFs are analyzed to extract structured data
- If successful, a new file is generated from the PDF content
- The new file replaces the PDF in the upload list
- Original PDF filename is preserved with indication of source

**Success Notification:**
- Green checkmark appears when parsing completes
- Notification shows: "Generated [filename] from PDF file upload"
- The generated file will have a detected type (e.g., .xml, .proto)

#### Step 3: Confirmation

<img src="${initModalConfirmUrl}" alt="Initialization Modal Confirmation" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

Before adding files to the canvas, review the summary:

**What You'll See:**
- Count of successfully uploaded files
- Brief explanation of next step

**Actions Available:**
1. **Successful upload count** - Shows how many files were successfully uploaded
2. **Add or Remove Recommended transforms** - DEXTER will recommend transform types based on the input files provided.
  You can use the Add or Remove buttons to select the final amount of nodes to be generated.
3. **Clear All** - Sets all recommended transform counts to 0
4. **Back** - Return to upload screen to add more files or make changes
5. **Cancel** - Discard all uploads and close the modal
6. **Finish** - Create input and transform nodes and add them to the canvas

#### Result: Automatic Node Creation

<img src="${initModalResultsUrl}" alt="Initialization Modal Results" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

When you click "Finish", Dexter automatically:

**Creates Input Nodes:**
- One input node is created for each successfully uploaded file, as long as the parser supports it
- Nodes are created with backend descriptors containing parsed data paths
- Each node displays the file structure and available data fields, as well as the source code

**Creates Transform Nodes:**
- The selected number and type of transform nodes are created.
- No connections are made yet, you will need to do that step in the Transform Engine

**Positions Nodes:**
- Nodes are arranged in rows across the canvas
- Spacing is optimized to prevent overlap
- Layout considers mapping canvas width and node sizes
- Wraps to new rows when reaching edge of mapping canvas

**Result:**
- Clean, organized layout ready for connecting transforms
- All files are in a collection for easy management
- You can immediately start building your transformation pipeline

---

## Select a Previous Transform Session

This option allows you to reload a previously saved mapping and continue working where you left off.

When you select "Select a Previous Transform Session", Dexter opens the saved mapping dialog, which displays all mappings that have been previously saved to your profile.

**From the dialog, you can:**
- Browse your saved mappings by name
- Select a mapping to load it onto the canvas

**Result:**
- The selected mapping is loaded onto the Transform Engine canvas
- All nodes and connections from the saved mapping are restored
- You can immediately continue building or editing your transformation pipeline

---

## Best Practices

### File Preparation

**Before Uploading:**
- Ensure files are valid and well-formed
- Remove any sensitive data if applicable
- Check file sizes are under 10MB limit
- Group related files together

**Naming Conventions:**
- Use descriptive filenames
- Avoid special characters that may cause issues
- Include file extensions

### File Type Verification

**Always Review:**
- Check that detected types match your expectations
- Correct any misidentifications before proceeding
- Pay special attention to files with ambiguous structures

**When to Override:**
- File has wrong extension but correct content
- Dexter misclassifies an uncommon file variant
- You know the correct format better than the classifier

### Managing Uploads

**During Upload:**
- Wait for all files to complete before clicking Next
- Watch for failed uploads (red alert icons)
- Remove failed files or retry upload
- Verify file types are detected before proceeding

**After Upload:**
- Review the confirmation screen carefully
- Ensure file count matches what you uploaded
- If count is wrong, go back and investigate

`;
